import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FilterBar,
  FilterField,
  Input,
  Pagination,
  RadixSelect,
  Skeleton,
  StatusBadge,
  toast,
  type DataTableColumn,
  type SelectOption,
} from '@/components/ui';
import {
  useSendHistoryListQuery,
  useSendHistoryRecipientsQuery,
} from '../hooks/useSendHistoryQuery';
import { useResendMessageMutation, ResendApiError } from '../hooks/useResendMessageMutation';
import { UserSearchSelect } from '@/features/users/components/UserSearchSelect';
import type { SendHistoryItem, SendHistoryPurpose, SendHistorySource } from '../types/notificationHistory';
import type { NotificationChannel } from '../types/notificationMessage';
import { SENDABLE_CHANNELS, channelLabel, isAllowlistBlocked, messageStatusLabel, messageStatusTone } from '../lib/notificationLabels';

const PAGE_SIZE = 20;
const RECIPIENT_PAGE_SIZE = 20;
const ALL = '';

const SOURCE_BADGE: Record<SendHistorySource, 'info' | 'warn' | 'neutral'> = {
  AUTO: 'info',
  BULK: 'warn',
  SINGLE: 'neutral',
};

const SOURCE_LABEL_KEY: Record<SendHistorySource, [string, string]> = {
  AUTO: ['history.type.auto', '자동 알림'],
  BULK: ['history.type.bulk', '대량 발송'],
  SINGLE: ['history.type.single', '단건 발송'],
};

/**
 * QA-427 — 유형 필터는 출처(자동/대량/단건)와 목적(정보성/광고성)을 한 드롭다운으로 고른다.
 * 값 'purpose:…' 은 BE purpose 파라미터로, 나머지는 source 로 보낸다.
 */
type TypeFilter = '' | SendHistorySource | 'purpose:INFORMATIONAL' | 'purpose:ADVERTISING';
type RecipientField = 'phone' | 'email';

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

/** 발송 내역 탭 — 이력 테이블 + 필터 + 발송건별 수신자 PII 드릴다운 + 단건 재발송. (디자인 스펙 §5) */
export const SendHistoryTab = () => {
  const { t } = useTranslation('notifications');

  const [typeFilter, setTypeFilter] = useState<TypeFilter>('');
  // QA-427 — 수신자(전화번호·이메일)·회원 검색. 입력은 [검색]/Enter 로 적용한다.
  const [recipientField, setRecipientField] = useState<RecipientField>('phone');
  const [recipientDraft, setRecipientDraft] = useState('');
  const [recipient, setRecipient] = useState('');
  const [userId, setUserId] = useState<string | null>(null);
  const [channel, setChannel] = useState<NotificationChannel | ''>('');
  const [status, setStatus] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(0);
  const [drilldown, setDrilldown] = useState<SendHistoryItem | null>(null);

  const filter = useMemo(
    () => ({
      ...(typeFilter.startsWith('purpose:')
        ? { purpose: typeFilter.slice('purpose:'.length) as SendHistoryPurpose }
        : typeFilter
          ? { source: typeFilter as SendHistorySource }
          : {}),
      ...(recipient ? { recipient } : {}),
      ...(userId ? { userId } : {}),
      ...(channel ? { channel } : {}),
      ...(status ? { status } : {}),
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
      page,
      size: PAGE_SIZE,
    }),
    [typeFilter, recipient, userId, channel, status, startDate, endDate, page],
  );

  const query = useSendHistoryListQuery(filter);
  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  const handleReset = useCallback(() => {
    setTypeFilter('');
    setRecipientDraft('');
    setRecipient('');
    setUserId(null);
    setChannel('');
    setStatus('');
    setStartDate('');
    setEndDate('');
    setPage(0);
  }, []);

  const channelOptions = useMemo<ReadonlyArray<SelectOption>>(
    () => [
      { value: ALL, label: t('common.all', '전체') },
      ...SENDABLE_CHANNELS.map((c) => ({ value: c, label: channelLabel(t, c) })),
    ],
    [t],
  );
  const sourceOptions = useMemo<ReadonlyArray<SelectOption>>(
    () => [
      { value: ALL, label: t('common.all', '전체') },
      { value: 'AUTO', label: t('history.type.auto', '자동 알림') },
      { value: 'BULK', label: t('history.type.bulk', '대량 발송') },
      { value: 'SINGLE', label: t('history.type.single', '단건 발송') },
      { value: 'purpose:INFORMATIONAL', label: t('history.type.informational', '정보성 메시지') },
      { value: 'purpose:ADVERTISING', label: t('history.type.advertising', '광고성 메시지') },
    ],
    [t],
  );
  const applyRecipient = () => {
    setRecipient(recipientDraft.trim());
    setPage(0);
  };
  const muted = (v: string | null | undefined) =>
    v ? <span className="text-[12px]">{v}</span> : <span style={{ color: 'var(--content-muted)' }}>—</span>;
  const statusOptions = useMemo<ReadonlyArray<SelectOption>>(
    () => [
      { value: ALL, label: t('common.all', '전체') },
      { value: 'PENDING', label: messageStatusLabel(t, 'PENDING') },
      { value: 'SENDING', label: messageStatusLabel(t, 'SENDING') },
      { value: 'SENT', label: messageStatusLabel(t, 'SENT') },
      { value: 'DELIVERED', label: messageStatusLabel(t, 'DELIVERED') },
      { value: 'FAILED', label: messageStatusLabel(t, 'FAILED') },
      { value: 'FALLBACK', label: messageStatusLabel(t, 'FALLBACK') },
    ],
    [t],
  );

  const columns: ReadonlyArray<DataTableColumn<SendHistoryItem>> = useMemo(
    () => [
      {
        // QA-427 — 맨 왼쪽 No(최신이 큰 번호 — 다른 목록과 같은 방식).
        key: 'no',
        header: 'No',
        align: 'center',
        width: 64,
        cell: (_row, i) => (
          <span className="tabular-nums text-xs" style={{ color: 'var(--content-muted)' }} data-testid="history-row-no">
            {total - (page * PAGE_SIZE + i)}
          </span>
        ),
      },
      {
        key: 'source',
        header: t('history.col.type', '유형'),
        cell: (row) =>
          row.source ? (
            <span className="inline-flex flex-wrap items-center gap-1">
              <Badge variant={SOURCE_BADGE[row.source]} size="sm">
                {row.sourceLabel ?? t(...SOURCE_LABEL_KEY[row.source])}
              </Badge>
              {/* QA-419 — 대량 발송 용도(정보성/광고성). 자동 알림·단건은 null 이라 표시하지 않는다. */}
              {row.purpose ? <PurposeBadge purpose={row.purpose} /> : null}
            </span>
          ) : (
            <span style={{ color: 'var(--content-muted)' }}>—</span>
          ),
        width: 140,
      },
      {
        key: 'channel',
        header: t('history.col.channel', '채널'),
        cell: (row) => <span className="text-[12px]">{row.channelLabel ?? channelLabel(t, row.channel)}</span>,
        width: 90,
      },
      // QA-427 — 자동·단건 행의 수신자. 대량 행은 [대상]을 눌러 수신자 목록에서 본다.
      { key: 'recipientLoginId', header: t('history.col.memberId', '회원ID'), cell: (row) => muted(row.recipientLoginId), width: 110 },
      // 운영 확인 — 수신자번호는 한 줄로(국가번호 포함 번호가 칸 폭에서 꺾였다).
      {
        key: 'recipientPhone',
        header: t('history.col.phone', '수신자번호'),
        cell: (row) =>
          row.recipientPhone ? (
            <span className="whitespace-nowrap text-[12px] tabular-nums" data-testid="history-recipient-phone">{row.recipientPhone}</span>
          ) : (
            muted(null)
          ),
        width: 150,
      },
      { key: 'recipientEmail', header: t('history.col.email', '이메일'), cell: (row) => muted(row.recipientEmail), width: 180 },
      {
        key: 'title',
        header: t('history.col.title', '제목'),
        cell: (row) => (
          <button
            type="button"
            onClick={() => setDrilldown(row)}
            className="max-w-[280px] truncate text-left text-[13px] underline-offset-2 hover:underline"
            style={{ color: 'var(--content-strong)' }}
          >
            {row.title?.trim() || '—'}
          </button>
        ),
      },
      {
        key: 'bodyPreview',
        header: t('history.col.body', '내용'),
        cell: (row) =>
          row.bodyPreview ? (
            <span className="block max-w-[260px] truncate text-[12px]" title={row.bodyPreview} style={{ color: 'var(--content-default)' }}>
              {row.bodyPreview}
            </span>
          ) : (
            <span style={{ color: 'var(--content-muted)' }}>—</span>
          ),
      },
      {
        key: 'recipientCount',
        header: t('history.col.recipientCount', '대상'),
        align: 'right',
        cell: (row) => (
          <button
            type="button"
            onClick={() => setDrilldown(row)}
            className="tabular-nums text-[12px] underline-offset-2 hover:underline"
            style={{ color: 'var(--content-default)' }}
          >
            {t('common.rowsTotal', '총 {{count}}건', { count: row.recipientCount })}
          </button>
        ),
        width: 96,
      },
      {
        key: 'result',
        header: t('history.col.result', '성공/실패'),
        cell: (row) => (
          <span className="text-[12px] tabular-nums">
            <span style={{ color: 'var(--content-default)' }}>{row.successCount}</span>
            {' · '}
            <span style={{ color: row.failCount > 0 ? 'var(--signal-danger)' : 'var(--content-muted)' }}>{row.failCount}</span>
          </span>
        ),
        width: 110,
      },
      {
        key: 'status',
        header: t('history.col.status', '상태'),
        cell: (row) =>
          row.status ? (
            <StatusBadge label={row.statusLabel ?? messageStatusLabel(t, row.status)} variant={messageStatusTone(row.status)} />
          ) : (
            <span style={{ color: 'var(--content-muted)' }}>—</span>
          ),
        width: 100,
      },
      {
        key: 'sentAt',
        header: t('history.col.sentAt', '발송일시'),
        cell: (row) => (
          <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
            {formatDateTime(row.sentAt ?? row.scheduledAt)}
          </span>
        ),
        width: 140,
      },
    ],
    [t, total, page],
  );

  return (
    <div className="flex flex-col gap-4" data-testid="send-history-tab">
      <FilterBar
        dense
        filters={
          <>
            <FilterField label={t('history.filter.type', '유형')}>
              <RadixSelect value={typeFilter} onChange={(v) => { setTypeFilter(v as TypeFilter); setPage(0); }} options={sourceOptions} aria-label={t('history.filter.type', '유형')} />
            </FilterField>
            <FilterField label={t('history.filter.recipient', '수신자')}>
              <div className="flex items-center gap-1.5">
                <RadixSelect
                  value={recipientField}
                  onChange={(v) => setRecipientField(v as RecipientField)}
                  options={[
                    { value: 'phone', label: t('history.filter.phone', '전화번호') },
                    { value: 'email', label: t('history.filter.email', '이메일') },
                  ]}
                  aria-label={t('history.filter.recipientField', '수신자 검색 기준')}
                />
                <Input
                  size="sm"
                  value={recipientDraft}
                  onChange={(e) => setRecipientDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applyRecipient();
                  }}
                  placeholder={recipientField === 'phone' ? '01012345678' : 'name@example.com'}
                  aria-label={t('history.filter.recipient', '수신자')}
                  data-testid="history-recipient-input"
                />
              </div>
            </FilterField>
            <FilterField label={t('history.filter.member', '회원')}>
              <div className="w-[280px] max-w-full">
                <UserSearchSelect
                  value={userId}
                  onChange={(id) => { setUserId(id); setPage(0); }}
                  testId="history-member-select"
                  ariaLabel={t('history.filter.member', '회원')}
                />
              </div>
            </FilterField>
            <FilterField label={t('history.filter.channel', '채널')}>
              <RadixSelect value={channel} onChange={(v) => { setChannel(v as NotificationChannel | ''); setPage(0); }} options={channelOptions} aria-label={t('history.filter.channel', '채널')} />
            </FilterField>
            <FilterField label={t('history.filter.status', '상태')}>
              <RadixSelect value={status} onChange={(v) => { setStatus(v); setPage(0); }} options={statusOptions} aria-label={t('history.filter.status', '상태')} />
            </FilterField>
            <FilterField label={t('history.filter.startDate', '시작일')}>
              <Input type="date" size="sm" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(0); }} aria-label={t('history.filter.startDate', '시작일')} />
            </FilterField>
            <FilterField label={t('history.filter.endDate', '종료일')}>
              <Input type="date" size="sm" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(0); }} aria-label={t('history.filter.endDate', '종료일')} />
            </FilterField>
          </>
        }
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => (recipientDraft.trim() !== recipient ? applyRecipient() : void query.refetch())}
              data-testid="history-search"
            >
              {t('common.search', '검색')}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleReset}>{t('common.reset', '초기화')}</Button>
          </>
        }
      />

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[36px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('history.error', '발송 내역을 불러오지 못했습니다.')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>{t('common.retry', '다시 시도')}</Button>
        </div>
      ) : items.length === 0 ? (
        <EmptyState title={t('history.empty', '발송 내역이 없습니다.')} />
      ) : (
        <DataTable
          columns={[...columns]}
          rows={[...items]}
          rowKey={(row) => row.historyId}
          stickyHeader
          mobileCardCollapse
          ariaLabel={t('history.title', '발송 내역')}
        />
      )}

      <Pagination total={total} currentPage={page + 1} pageSize={PAGE_SIZE} onPageChange={(p) => setPage(p - 1)} className="mt-1" />

      <RecipientDrilldownModal item={drilldown} onClose={() => setDrilldown(null)} onOpen={setDrilldown} />
    </div>
  );
};

const PurposeBadge = ({ purpose }: { purpose: SendHistoryPurpose }) => {
  const { t } = useTranslation('notifications');
  return (
    <Badge variant={purpose === 'INFORMATIONAL' ? 'info' : 'neutral'} size="sm" data-testid="history-purpose">
      {purpose === 'INFORMATIONAL'
        ? t('history.purpose.INFORMATIONAL', '정보성')
        : t('history.purpose.ADVERTISING', '광고성')}
    </Badge>
  );
};

interface RecipientDrilldownModalProps {
  item: SendHistoryItem | null;
  onClose: () => void;
  /** 연결된 건(대체 문자 ↔ 원래 알림톡)으로 바꿔 연다. */
  onOpen?: (item: SendHistoryItem) => void;
}

const RecipientDrilldownModal = ({ item, onClose, onOpen }: RecipientDrilldownModalProps) => {
  const { t } = useTranslation('notifications');
  const [page, setPage] = useState(0);
  const resend = useResendMessageMutation();

  useEffect(() => {
    if (item) setPage(0);
  }, [item]);

  const query = useSendHistoryRecipientsQuery(item?.historyId ?? null, page, RECIPIENT_PAGE_SIZE);
  const recipients = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  const handleResend = (recipientId: string) => {
    resend.mutate(recipientId, {
      onSuccess: () => {
        toast.success(t('history.resend.success', '재발송 요청이 접수되었습니다. 잠시 후 상태가 갱신됩니다.'));
        void query.refetch();
      },
      onError: (err) => {
        const e = err as ResendApiError;
        toast.error(
          e.isConflict()
            ? t('history.resend.errorConflict', '이미 발송되었거나 재발송할 수 없는 상태의 메시지입니다.')
            : e.isForbidden()
              ? t('history.resend.errorForbidden', '재발송 권한이 없습니다.')
              : t('history.resend.errorGeneric', '재발송에 실패했습니다. 잠시 후 다시 시도해 주세요.'),
        );
      },
    });
  };

  return (
    <Dialog open={item !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('history.recipients.title', '수신자 목록')}</DialogTitle>
          <DialogDescription>
            {(item?.title?.trim() || '—')} · {t('common.rowsTotal', '총 {{count}}건', { count: total })}
          </DialogDescription>
          {/* notification #28 — 알림톡 대체발송 ↔ 대체 문자 오가기. */}
          {item?.relatedHistoryId && onOpen ? (
            <div>
              <Button
                variant="outline"
                size="sm"
                data-testid="history-related-link"
                onClick={() =>
                  onOpen({
                    ...item,
                    historyId: item.relatedHistoryId as string,
                    relatedHistoryId: item.historyId,
                    status: null,
                    statusLabel: null,
                  })
                }
              >
                {item.status === 'FALLBACK'
                  ? t('history.related.toFallbackSms', '대체 문자 보기')
                  : t('history.related.toOriginal', '원래 알림톡 보기')}
              </Button>
            </div>
          ) : null}
        </DialogHeader>

        {query.isPending ? (
          <div className="space-y-2" role="status" aria-live="polite">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-[32px] w-full" />
            ))}
          </div>
        ) : query.isError ? (
          <EmptyState size="inline" title={t('history.recipients.error', '수신자 목록을 불러오지 못했습니다.')} />
        ) : recipients.length === 0 ? (
          <EmptyState size="inline" title={t('history.recipients.empty', '수신자 정보가 없습니다.')} />
        ) : (
          <div className="overflow-hidden rounded-md" style={{ border: '1px solid var(--divider-strong)' }}>
            <table className="w-full text-[12px]">
              <thead>
                <tr style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}>
                  <th className="px-3 py-2 text-left font-medium">{t('history.recipients.col.loginId', '회원 ID')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('history.recipients.col.name', '이름')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('history.recipients.col.contact', '수신처')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('history.recipients.col.status', '상태')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('history.recipients.col.error', '실패 사유')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('history.recipients.col.sentAt', '발송일시')}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {recipients.map((r) => (
                  <tr key={r.recipientId} style={{ borderTop: '1px solid var(--divider-soft)' }}>
                    <td className="px-3 py-2" style={{ color: 'var(--content-default)' }} data-testid="history-recipient-login-id">{r.loginId ?? '—'}</td>
                    <td className="px-3 py-2" style={{ color: 'var(--content-strong)' }}>{r.maskedName ?? '—'}</td>
                    <td className="px-3 py-2 font-mono" style={{ color: 'var(--content-default)' }}>{r.maskedContact ?? '—'}</td>
                    <td className="px-3 py-2">
                      {r.status ? (
                        <StatusBadge size="sm" label={r.statusLabel ?? messageStatusLabel(t, r.status)} variant={messageStatusTone(r.status)} />
                      ) : (
                        <span style={{ color: 'var(--content-muted)' }}>—</span>
                      )}
                    </td>
                    <td className="px-3 py-2" style={{ color: 'var(--content-muted)' }}>
                      {/* 수신자 허용 목록(테스트 모드) 차단 — BE providerCode ALLOWLIST_BLOCKED, 사유 문구에 "허용 목록". */}
                      {isAllowlistBlocked(r.errorMessage) ? (
                        <Badge variant="warn" size="sm" data-testid="history-allowlist-blocked">
                          {t('history.allowlistBlocked', '허용 목록 차단')}
                        </Badge>
                      ) : (
                        r.errorMessage ?? '—'
                      )}
                    </td>
                    <td className="px-3 py-2 tabular-nums" style={{ color: 'var(--content-muted)' }}>{formatDateTime(r.deliveredAt ?? r.sentAt)}</td>
                    <td className="px-3 py-2 text-right">
                      {r.status === 'FAILED' ? (
                        <Button variant="outline" size="sm" disabled={resend.isPending} onClick={() => handleResend(r.recipientId)}>
                          {t('history.resend.action', '재발송')}
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination total={total} currentPage={page + 1} pageSize={RECIPIENT_PAGE_SIZE} onPageChange={(p) => setPage(p - 1)} className="mt-1" />

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" size="sm">{t('common.close', '닫기')}</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
